import React from 'react';
import './ProfileCard.css';
import { ProfileCard as ProfileCardType } from '../types';

interface ProfileCardProps {
  card: ProfileCardType;
  onInfoClick: () => void;
}

const ProfileCard: React.FC<ProfileCardProps> = ({ card, onInfoClick }) => {
  // Handle script loading for widgets
  React.useEffect(() => {
    if (card.scriptUrl) {
      const existingScript = document.querySelector(`script[src="${card.scriptUrl}"]`);
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = card.scriptUrl;
        script.async = true;
        document.body.appendChild(script);
      }
    }
  }, [card.scriptUrl]);


  const renderCardContent = () => {
    switch (card.type) {
      case 'numbers':
        return (
          <div className="card-content">
            <div className="card-title">{card.title}</div>
            <div style={{ marginTop: '16px' }}>
              {card.items?.map((item, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>{item.label}</span>
                  <span>{item.value}</span>
                </div>
              ))}
              {card.special && (
                <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>{card.special.label}</span>
                    <span style={{ fontWeight: '600' }}>{card.special.value}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'embed':
        if (card.embedType === 'spotify') {
          // If iframe URL is provided, use real Spotify embed
          if (card.iframeUrl) {
            return (
              <div className="spotify-embed">
                <iframe
                  src={card.iframeUrl}
                  width="100%"
                  height="352"
                  frameBorder="0"
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  style={{ borderRadius: '12px', border: 'none' }}
                  title="Spotify player"
                />
              </div>
            );
          }
          
          // Otherwise use custom Spotify-style player visual
          return (
            <div className="spotify-embed">
              <div className="spotify-player">
                <div className="album-art">
                  {card.albumArt ? (
                    <img src={card.albumArt} alt="Album Art" />
                  ) : (
                    <div className="default-album-art">
                      <svg width="60" height="60" viewBox="0 0 24 24" fill="white">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                      </svg>
                    </div>
                  )}
                </div>
                
                <div className="track-info">
                  <div className="track-title">
                    {card.trackTitle || card.title || "Currently Playing"}
                  </div>
                  <div className="artist-name">
                    {card.artist || card.subtitle || "Unknown Artist"}
                  </div>
                </div>
                
                <div className="spotify-controls">
                  <svg className="control-btn" width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                  </svg>
                  <svg className="control-btn play-btn" width="32" height="32" viewBox="0 0 24 24" fill="white">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                  <svg className="control-btn" width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
                  </svg>
                </div>
                
                <div className="progress-bar">
                  <div className="progress-track">
                    <div className="progress-fill" style={{width: '35%'}}></div>
                  </div>
                  <div className="time-stamps">
                    <span>1:23</span>
                    <span>3:45</span>
                  </div>
                </div>
                
                <div className="spotify-logo">
                  {card.icon} {card.service}
                </div>
              </div>
            </div>
          );
        }
        
        if (card.embedType === 'hulu') {
          return (
            <div className="hulu-embed">
              <div className="hulu-content">
                <div className="hulu-header">
                  <span className="hulu-icon">{card.icon}</span>
                  <div className="hulu-info">
                    <div className="hulu-title">{card.title}</div>
                    <div className="hulu-subtitle">{card.subtitle}</div>
                  </div>
                  <div className="hulu-logo">hulu</div>
                </div>
                
                {card.htmlContent && (
                  <div 
                    className="hulu-iframe-container"
                    dangerouslySetInnerHTML={{ __html: card.htmlContent }}
                  />
                )}
              </div>
            </div>
          );
        }
        
        if (card.embedType === 'goodreads') {
          return (
            <div className="goodreads-embed">
              <div className="goodreads-content">
                <div className="goodreads-header">
                  <span className="goodreads-icon">{card.icon}</span>
                  <div className="goodreads-info">
                    <div className="goodreads-title">{card.title}</div>
                    <div className="goodreads-subtitle">{card.subtitle}</div>
                  </div>
                  <div className="goodreads-logo">📚 Goodreads</div>
                </div>
                
                <div className="book-showcase">
                  {card.books?.map((book, index) => (
                    <div key={index} className="book-item">
                      <div className="book-cover">{book.cover}</div>
                      <div className="book-details">
                        <div className="book-title">{book.title}</div>
                        <div className="book-author">by {book.author}</div>
                        <div className="book-rating">
                          {'⭐'.repeat(book.rating)}
                        </div>
                        <div className="book-status">{book.status}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        }
        
        if (card.embedType === 'map') {
          // Use interactive map if enabled and locations are provided
          if (card.useInteractiveMap && card.mapLocations) {
            const InteractiveMap = React.lazy(() => import('./InteractiveMap'));
            
            return (
              <div className="map-embed">
                <div className="map-container">
                  <React.Suspense fallback={<div className="map-loading">Loading map...</div>}>
                    <InteractiveMap 
                      locations={card.mapLocations} 
                      height="220px" 
                    />
                  </React.Suspense>
                  
                  <div className="map-overlay">
                    <div className="map-title">
                      {card.icon} {card.title}
                    </div>
                    <div className="map-subtitle">
                      {card.subtitle}
                    </div>
                    <div className="map-service">
                      {card.service}
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          
          // Fallback to SVG map
          return (
            <div className="map-embed">
              <div className="map-container">
                <svg className="map-svg" viewBox="0 0 300 220" fill="none">
                  {/* Map background with Apple Maps-style colors */}
                  <rect width="300" height="220" fill="#1a1a1a"/>
                  
                  {/* Roads and streets */}
                  <path d="M0 80 Q50 75 100 80 T200 85 L300 90" stroke="#4a4a4a" strokeWidth="3" fill="none"/>
                  <path d="M50 0 L60 220" stroke="#4a4a4a" strokeWidth="2" fill="none"/>
                  <path d="M150 0 L160 220" stroke="#4a4a4a" strokeWidth="2" fill="none"/>
                  <path d="M0 140 Q80 135 160 140 T300 145" stroke="#4a4a4a" strokeWidth="2" fill="none"/>
                  
                  {/* Water bodies */}
                  <ellipse cx="220" cy="60" rx="40" ry="25" fill="#2d5aa0"/>
                  
                  {/* Parks/green areas */}
                  <rect x="80" y="100" width="60" height="40" rx="8" fill="#3d5a3d"/>
                  <rect x="10" y="160" width="40" height="30" rx="6" fill="#3d5a3d"/>
                  
                  {/* Buildings */}
                  <rect x="170" y="95" width="15" height="20" fill="#666"/>
                  <rect x="190" y="90" width="20" height="25" fill="#666"/>
                  <rect x="215" y="100" width="12" height="15" fill="#666"/>
                  
                  {/* Location pins for the three cities */}
                  {/* China pin */}
                  <g transform="translate(80, 50)">
                    <circle cx="0" cy="8" r="12" fill="#ff3b30"/>
                    <circle cx="0" cy="8" r="8" fill="white"/>
                    <circle cx="0" cy="8" r="4" fill="#ff3b30"/>
                    <path d="M0 20 L-6 8 L6 8 Z" fill="#ff3b30"/>
                  </g>
                  
                  {/* California pin */}
                  <g transform="translate(160, 120)">
                    <circle cx="0" cy="8" r="12" fill="#ff3b30"/>
                    <circle cx="0" cy="8" r="8" fill="white"/>
                    <circle cx="0" cy="8" r="4" fill="#ff3b30"/>
                    <path d="M0 20 L-6 8 L6 8 Z" fill="#ff3b30"/>
                  </g>
                  
                  {/* Bangalore pin */}
                  <g transform="translate(240, 170)">
                    <circle cx="0" cy="8" r="12" fill="#ff3b30"/>
                    <circle cx="0" cy="8" r="8" fill="white"/>
                    <circle cx="0" cy="8" r="4" fill="#ff3b30"/>
                    <path d="M0 20 L-6 8 L6 8 Z" fill="#ff3b30"/>
                  </g>
                  
                  {/* Connection lines between cities */}
                  <path d="M80 58 Q120 80 160 128" stroke="#007aff" strokeWidth="2" fill="none" strokeDasharray="4,4" opacity="0.8"/>
                  <path d="M160 128 Q200 150 240 178" stroke="#007aff" strokeWidth="2" fill="none" strokeDasharray="4,4" opacity="0.8"/>
                </svg>
                
                <div className="map-overlay">
                  <div className="map-title">
                    {card.icon} {card.title}
                  </div>
                  <div className="map-subtitle">
                    {card.subtitle}
                  </div>
                  <div className="map-service">
                    {card.service}
                  </div>
                </div>
              </div>
            </div>
          );
        }
        
        // Handle HTML widget content (like Goodreads)
        if (card.htmlContent && card.embedType !== 'hulu') {
          return (
            <div className={`${card.embedType}-embed`}>
              <div 
                className="widget-container"
                dangerouslySetInnerHTML={{ __html: card.htmlContent }}
              />
            </div>
          );
        }
        
        return (
          <div className={`${card.embedType}-embed`}>
            <div style={{ padding: '20px', color: 'white', textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                {card.icon} {card.title}
              </div>
              <div style={{ fontSize: '14px', opacity: '0.8', marginBottom: '8px' }}>
                {card.subtitle}
              </div>
              <div style={{ marginTop: '16px', fontSize: '12px' }}>{card.service}</div>
            </div>
          </div>
        );

      case 'quote':
        return (
          <div className="card-content">
            {card.title && <div className="card-title">{card.title}</div>}
            <div className="quote-text">{card.quote}</div>
            <div className="quote-author">— {card.author}</div>
          </div>
        );

      case 'image':
        return (
          <div 
            style={{
              height: card.height || '280px',
              background: card.background || 'linear-gradient(135deg, #e0e0e0, #bdbdbd)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: card.fontSize || '48px'
            }}
          >
            {card.content}
          </div>
        );

      case 'interactions':
        const generateRandomTime = () => {
          const hours = Math.floor(Math.random() * 12) + 1;
          const minutes = Math.floor(Math.random() * 60);
          const ampm = Math.random() > 0.5 ? 'AM' : 'PM';
          return `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
        };

        return (
          <div className="card-content">
            <div className="card-title">{card.title}</div>
            {card.subtitle && <div className="card-subtitle">{card.subtitle}</div>}
            
            {card.interactions && (
              <div className="interactions-list">
                {card.interactions.map((interaction, index) => (
                  <div key={index} className="interaction-item">
                    <div className="interaction-header">
                      <span className="interaction-icon">{interaction.icon}</span>
                      <span className="interaction-app">{interaction.app}</span>
                      <span className="interaction-time">{generateRandomTime()}</span>
                    </div>
                    <div className="interaction-activity">{interaction.activity}</div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="connect-button">
              <div className="connect-status">
                <div className="status-indicator"></div>
                <span>Connected via Special App</span>
              </div>
            </div>
          </div>
        );

      case 'photos':
        return (
          <div className="card-content">
            <div className="card-title">{card.title}</div>
            {card.artImages && (
              <div className="art-gallery">
                {card.artImages.map((imageUrl, index) => (
                  <div key={index} className="art-image-container">
                    <img 
                      src={imageUrl} 
                      alt={`Art piece ${index + 1}`}
                      className="art-image"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'simple':
      default:
        return (
          <div className="card-content">
            <div className="card-title">{card.title}</div>
            {card.subtitle && <div className="card-subtitle">{card.subtitle}</div>}
            {card.text && <div className="card-text">{card.text}</div>}
          </div>
        );
    }
  };

  return (
    <div 
      className={`card ${card.type === 'quote' ? 'quote-card' : ''}`}
      style={card.style ? (() => {
        try {
          return { ...JSON.parse(card.style) };
        } catch (e) {
          console.warn('Invalid JSON in card.style:', card.style);
          return {};
        }
      })() : {}}
    >
      <button className={`info-btn ${['quote', 'embed', 'interactions'].includes(card.type) ? 'info-btn-text' : ''}`} onClick={onInfoClick}>
        {['quote', 'embed', 'interactions'].includes(card.type) ? 'click to chat' : 'i'}
      </button>
      {renderCardContent()}
    </div>
  );
};

export default ProfileCard;